import globalRenderer from '@/shared/misc/global-renderer'
import PostEffectManager from './posteffects/posteffects-manager'
import {
  Clock,
  Color,
  DynamicDrawUsage,
  Fog,
  InstancedMesh,
  Mesh,
  Object3D,
  PerspectiveCamera,
  PointLight,
  Scene
} from 'three'
import sizeSystem from '@/shared/size-system'
import assetsSystem from '@/shared/assets-system'
import coinFactory from '@/shared/common/coin-factory'
import CoinDefinition from '@/shared/common/coin-definition'
import coinMaterial from './materials/coin-material'
import soundSystem from '@/shared/sound-system'
import query from '@/shared/misc/query'
import Denomination from '@/shared/common/denomination'

const amount = 10
const count = Math.pow(amount, 3)
const dummy = new Object3D()
const randomSeed: number[] = []
for (let index = 0; index < count; index++) {
  randomSeed.push(Math.random())
  randomSeed.push(Math.random())
  randomSeed.push(Math.random())
}

export default class InstancedCoins {
  private running = true
  private clock = new Clock()
  private time = 0
  private renderer
  private postEffectManager
  private scene
  private camera
  private bloomListIsDirty = true
  private coins: InstancedMesh | undefined
  private light: PointLight

  constructor() {
    this.renderer = globalRenderer.getRenderer()
    this.postEffectManager = new PostEffectManager(this.renderer)
    this.scene = new Scene()
    this.scene.background = new Color(0x000000)
    this.scene.fog = new Fog(0x000000, 15, 20)
    this.camera = new PerspectiveCamera(23, window.innerWidth / window.innerHeight, 0.1, 50)
    this.postEffectManager.setSceneAndCamera(this.scene, this.camera)
    this.postEffectManager.updateBloomList()

    this.camera.position.z = 5

    const light = new PointLight(0xffffff, 1.0)
    light.position.set(0, 0, -3)
    this.light = light
    //this.scene.add(light)

    const d = query.get('d') as string
    let finalD
    if (d) {
      let dAsNumber = parseInt(d)
      if (dAsNumber < 100) {
        dAsNumber = 100
      } else if (dAsNumber > 400000) {
        dAsNumber = 400000
      }
      finalD = dAsNumber as Denomination
    } else {
      finalD = Denomination.Ten
    }

    const definition = coinFactory.getCoinDefinition(finalD)
    assetsSystem.loadSingleCoin(definition).then(() => {
      const model = assetsSystem.getModel(definition.modelName) as Object3D

      this.processCoin(model, definition)

      this.gameLoop()
    })

    soundSystem.changeAmbience(['ambience_vault'])

    sizeSystem.onResize.add(this.resize)
    this.resize()
  }

  private processCoin(model: Object3D, definition: CoinDefinition) {
    const loadedMesh = model.clone().children.filter((x: Object3D) => x.type === 'Mesh')[0] as Mesh
    const mat = coinMaterial.getMaterial(definition.normalMap, definition.diffuseMap, definition.gemColor)
    const geo = loadedMesh.geometry
    geo.scale(15, 15, 15)
    const coinMesh = new InstancedMesh(loadedMesh.geometry, mat, count)
    coinMesh.instanceMatrix.setUsage(DynamicDrawUsage)
    coinMesh.castShadow = true
    coinMesh.receiveShadow = true

    /*coinMesh.scale.setScalar(100)
    coinMesh.rotation.x = Math.PI * 0.5
    coinMesh.castShadow = false
    coinMesh.receiveShadow = false
    coinMesh.userData.enableBloom = true
*/
    this.scene.add(coinMesh)

    coinMesh.material = mat
    this.coins = coinMesh
  }

  private gameLoop = () => {
    if (this.running) {
      requestAnimationFrame(this.gameLoop)
    }

    let delta = this.clock.getDelta()

    if (delta > 0.027) {
      delta = 0.027
    }

    this.time += delta
    this.update(this.time, delta)
  }

  update(time: number, delta: number): void {
    if (this.coins) {
      this.coins.rotation.z += 0.0003
      this.camera.rotation.y = Math.sin(((time * 0.1) % 13) * Math.PI * 0.5)
      this.camera.position.x = Math.sin(((time * 0.1) % 13) * Math.PI * 0.5) * 10
      //this.light.position.x = Math.sin(((time * 0.1) % 13) * Math.PI * 0.5) * -10
      let i = 0
      const offset = (amount - 1) / 2

      for (let x = 0; x < amount; x++) {
        for (let y = 0; y < amount; y++) {
          for (let z = 0; z < amount; z++) {
            dummy.position.x = offset - x + (randomSeed[i * 3] - 0.5)
            dummy.position.y = offset - z + (randomSeed[i * 3 + 1] - 0.5)
            dummy.position.z =
              ((time * (0.3 + 0.3 * randomSeed[z]) + randomSeed[i * 3 + 2] * 10) % 6) * 4 -
              16 +
              randomSeed[i * 3 + 2] * 0.25

            dummy.rotation.x = time + randomSeed[i * 3 + 1] * 20
            //dummy.rotation.y = time + randomSeed[i] * 20
            dummy.rotation.z = time + randomSeed[i * 3 + 2] * 30

            dummy.updateMatrix()

            this.coins.setMatrixAt(i++, dummy.matrix)
          }
        }
      }

      this.coins.instanceMatrix.needsUpdate = true
    }
    if (globalRenderer.usePostEffects) {
      if (this.bloomListIsDirty) {
        this.bloomListIsDirty = false
      }

      this.postEffectManager.render(time, delta)
    } else {
      this.renderer.render(this.scene, this.camera)
    }

    //this.rendererDebugInfo.update(delta)
    this.renderer.info.reset()
  }

  resize = () => {
    const size = sizeSystem.size
    this.renderer.setSize(size.width, size.height)

    this.postEffectManager.resize()

    const cameraAsPerspectiveType = this.camera as PerspectiveCamera
    cameraAsPerspectiveType.aspect = size.width / size.height
    cameraAsPerspectiveType.updateProjectionMatrix()
  }
}
